import { Order, Address } from '../types';

/**
 * Type that removes null from all properties recursively
 */
type DeepNullToUndefined<T> = T extends null
  ? undefined
  : T extends (infer U)[]
    ? DeepNullToUndefined<U>[]
    : T extends object
      ? { [K in keyof T]: DeepNullToUndefined<T[K]> }
      : T;

/**
 * Recursively converts null values to undefined in an object
 * This helps with form controls and validation schemas that expect undefined for optional values
 */
export function nullsToUndefined<T>(obj: T): DeepNullToUndefined<T> {
  if (obj === null) {
    return undefined as DeepNullToUndefined<T>;
  }

  if (Array.isArray(obj)) {
    return obj.map(nullsToUndefined) as DeepNullToUndefined<T>;
  }

  if (typeof obj === 'object') {
    const result = {} as DeepNullToUndefined<T>;
    for (const [key, value] of Object.entries(obj)) {
      (result as Record<string, unknown>)[key] = nullsToUndefined(value);
    }
    return result;
  }

  return obj as DeepNullToUndefined<T>;
}

/**
 * Specific transformer for Order objects.
 * Reconstructs nested objects (jobLocation, asceData) from the flat fields
 * returned by the backend API.
 */
export function transformOrderFromAPI(order: Order): DeepNullToUndefined<Order> {
  const raw = order as unknown as Record<string, unknown>;

  // Reconstruct nested jobLocation from flat backend fields
  if (raw.jobLocationAddress1 || raw.jobLocationLatitude) {
    order.jobLocation = {
      address1: (raw.jobLocationAddress1 as string) || '',
      address2: (raw.jobLocationAddress2 as string) || undefined,
      city: (raw.jobLocationCity as string) || '',
      state: (raw.jobLocationState as string) || '',
      zip: (raw.jobLocationZip as string) || '',
      latitude: raw.jobLocationLatitude as number | undefined,
      longitude: raw.jobLocationLongitude as number | undefined,
      validationStatus:
        (raw.jobLocationValidationStatus as 'none' | 'validated' | 'failed') || 'none',
      validationMode: (raw.jobLocationValidationMode as 'address' | 'coordinates') || 'address',
    };
  }

  // Reconstruct nested asceData from flat backend fields
  if (raw.asceRiskLevel || raw.asceElevation) {
    let mriWindResults: Record<string, string> | undefined;
    if (raw.asceMriWindResultsJson) {
      try {
        mriWindResults =
          typeof raw.asceMriWindResultsJson === 'string'
            ? JSON.parse(raw.asceMriWindResultsJson)
            : (raw.asceMriWindResultsJson as Record<string, string>);
      } catch {
        mriWindResults = undefined;
      }
    }

    order.asceData = {
      windLoadMph: (order.windLoad as number) || 0,
      snowLoadPsf: (order.snowLoad as number) || 0,
      elevation: raw.asceElevation as string | undefined,
      riskLevel: (raw.asceRiskLevel as number) || 1,
      isHurricaneWindDebrisZone: raw.asceIsHurricaneWindDebrisZone as boolean | undefined,
      isSpecialWindZone: raw.asceIsSpecialWindZone as boolean | undefined,
      isNewHampshire_7_16: raw.asceIsNewHampshire_7_16 as boolean | undefined,
      mriWindResults,
      lastUpdated: raw.asceLastUpdated as string | undefined,
    };
  }

  if (raw.addressValidationOverride !== undefined) {
    order.addressValidationOverride = raw.addressValidationOverride as boolean;
  }
  if (raw.manualLoadRatingCertified !== undefined) {
    order.manualLoadRatingCertified = raw.manualLoadRatingCertified as boolean;
  }

  return nullsToUndefined(order);
}

/**
 * Flattens nested jobLocation and asceData objects into the flat fields
 * expected by the backend API before sending.
 */
export function flattenOrderForAPI(order: Record<string, unknown>): Record<string, unknown> {
  const result = { ...order };

  // Flatten jobLocation
  const jobLoc = result.jobLocation as Record<string, unknown> | undefined;
  if (jobLoc) {
    result.jobLocationAddress1 = jobLoc.address1 || '';
    result.jobLocationAddress2 = jobLoc.address2 || '';
    result.jobLocationCity = jobLoc.city || '';
    result.jobLocationState = jobLoc.state || '';
    result.jobLocationZip = jobLoc.zip || '';
    result.jobLocationLatitude = jobLoc.latitude;
    result.jobLocationLongitude = jobLoc.longitude;
    result.jobLocationValidationStatus = jobLoc.validationStatus || 'none';
    result.jobLocationValidationMode = jobLoc.validationMode || 'address';
  }
  delete result.jobLocation;

  // Flatten asceData
  const asce = result.asceData as Record<string, unknown> | undefined;
  if (asce) {
    result.asceElevation = asce.elevation ?? null;
    result.asceRiskLevel = asce.riskLevel || 1;
    result.asceIsHurricaneWindDebrisZone = asce.isHurricaneWindDebrisZone || false;
    result.asceIsSpecialWindZone = asce.isSpecialWindZone || false;
    result.asceIsNewHampshire_7_16 = asce.isNewHampshire_7_16 || false;
    result.asceMriWindResultsJson = asce.mriWindResults
      ? JSON.stringify(asce.mriWindResults)
      : null;
  }
  delete result.asceData;

  return result;
}

/**
 * Specific transformer for Address objects to ensure form compatibility
 */
export function transformAddressForForm(
  address: Address | null | undefined
): DeepNullToUndefined<Address> | undefined {
  if (!address) return undefined;
  return nullsToUndefined(address);
}
